import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Menu,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Select,
  TableSortLabel
} from '@mui/material';
import { Plus, Pencil, Trash, DotsThreeVertical, Package, ArrowLeft } from '@phosphor-icons/react';
import api from '../../../utils/api';
import { useBusiness } from '../../../context/BusinessContext';
import ProductPriceBreakdown, { getProductPriceTotals } from './ProductPriceBreakdown';

function ProductList() {
  const navigate = useNavigate();
  const { selectedBusiness, loading: businessLoading } = useBusiness();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const businessId = selectedBusiness?.business?._id;
      if (!businessId) {
        setLoading(false);
        return;
      }
      const response = await api.get(`/api/products/business/${businessId}`);
      setProducts(response.data || []);
    } catch (err) {
      setError('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness?.business?._id]);

  useEffect(() => {
    if (businessLoading) return;
    const businessId = selectedBusiness?.business?._id;
    if (businessId) fetchProducts();
  }, [businessLoading, selectedBusiness?.business?._id, fetchProducts]);

  const handleMenuOpen = (event, product) => {
    setAnchorEl(event.currentTarget);
    setSelectedProduct(product);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProduct(null);
  };

  const handleDelete = async () => {
    if (!selectedProduct) return;
    try {
      await api.delete(`/api/products/${selectedProduct._id}`);
      fetchProducts();
      handleMenuClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete product');
    }
  };

  const categories = useMemo(() => {
    const uniqueCategories = new Set(
      products
        .map((product) => product.category?.trim())
        .filter(Boolean)
    );
    return Array.from(uniqueCategories).sort((a, b) => a.localeCompare(b));
  }, [products]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection((previousDirection) => (previousDirection === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortBy(field);
    setSortDirection('asc');
  };

  const filteredAndSortedProducts = useMemo(() => {
    const normalisedSearch = searchTerm.trim().toLowerCase();

    const filtered = products.filter((product) => {
      const matchesSearch = !normalisedSearch || [
        product.name,
        product.description,
        product.category
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(normalisedSearch));

      const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;

      return matchesSearch && matchesCategory;
    });

    return [...filtered].sort((a, b) => {
      let aValue;
      let bValue;

      switch (sortBy) {
        case 'price':
          aValue = getProductPriceTotals(a).total;
          bValue = getProductPriceTotals(b).total;
          break;
        case 'category':
          aValue = a.category || '';
          bValue = b.category || '';
          break;
        case 'isOptional':
          aValue = a.isOptional ? 1 : 0;
          bValue = b.isOptional ? 1 : 0;
          break;
        case 'name':
        default:
          aValue = a.name || '';
          bValue = b.name || '';
          break;
      }

      if (typeof aValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      const comparison = aValue - bValue;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [products, searchTerm, categoryFilter, sortBy, sortDirection]);

  const businessId = selectedBusiness?.business?._id;

  if (businessLoading || !businessId) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton
            onClick={() => navigate('/supplier/agreements')}
            aria-label="Back"
          >
            <ArrowLeft size={20} />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Products & Services
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" justifyContent="flex-end">
          {!loading && products.length > 0 && (
            <>
              <TextField
                size="small"
                label="Search products"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                sx={{ minWidth: 240 }}
              />
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="product-category-filter-label">Category</InputLabel>
                <Select
                  labelId="product-category-filter-label"
                  label="Category"
                  value={categoryFilter}
                  onChange={(event) => setCategoryFilter(event.target.value)}
                >
                  <MenuItem value="all">All categories</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
          <Button
            variant="contained"
            startIcon={<Plus size={20} />}
            onClick={() => navigate('/supplier/products/new')}
          >
            Add Product
          </Button>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Manage your products and services here so you can quickly build accurate agreements.
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ overflow: 'hidden' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : products.length === 0 ? (
          <Box py={6} textAlign="center">
            <Package size={48} weight="light" style={{ opacity: 0.5, marginBottom: 16 }} />
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No products yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create reusable products to quickly build agreements
            </Typography>
            <Button
              variant="contained"
              startIcon={<Plus size={18} />}
              onClick={() => navigate('/supplier/products/new')}
            >
              Add your first product
            </Button>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={sortBy === 'name' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'name'}
                      direction={sortBy === 'name' ? sortDirection : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell sortDirection={sortBy === 'price' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'price'}
                      direction={sortBy === 'price' ? sortDirection : 'asc'}
                      onClick={() => handleSort('price')}
                    >
                      Price / total
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'category' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'category'}
                      direction={sortBy === 'category' ? sortDirection : 'asc'}
                      onClick={() => handleSort('category')}
                    >
                      Category
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sortDirection={sortBy === 'isOptional' ? sortDirection : false}>
                    <TableSortLabel
                      active={sortBy === 'isOptional'}
                      direction={sortBy === 'isOptional' ? sortDirection : 'asc'}
                      onClick={() => handleSort('isOptional')}
                    >
                      Optional
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" width={60} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSortedProducts.map((product) => (
                  <TableRow key={product._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {product.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                        {product.description || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <ProductPriceBreakdown
                        price={product.price}
                        taxPercentage={product.taxPercentage}
                        priceIncludesTax={product.priceIncludesTax}
                        currency={product.currency}
                      />
                    </TableCell>
                    <TableCell>{product.category || '—'}</TableCell>
                    <TableCell align="center">
                      {product.isOptional ? (
                        <Chip label="Optional" size="small" variant="outlined" />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, product)}
                        aria-label="Actions"
                      >
                        <DotsThreeVertical size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {!loading && products.length > 0 && filteredAndSortedProducts.length === 0 && (
          <Box py={5} textAlign="center">
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No matching products found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try changing your search or category filter
            </Typography>
          </Box>
        )}
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            navigate(`/supplier/products/${selectedProduct?._id}/edit`);
            handleMenuClose();
          }}
        >
          <Pencil size={18} style={{ marginRight: 8 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <Trash size={18} style={{ marginRight: 8 }} />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default ProductList;
